import React, {useMemo} from 'react';
import {FormInputComponent} from "../../../components/form/Form";
import {InputNumber, Radio, Space} from "antd";
import {useOutletContext} from "react-router-dom";
import {usePositionForm} from "../../../providers/PositionFormProvider";
import {useAuth} from "../../../providers/AuthProvider";
import Project from "../../../models/Project";
import {useProjectForm} from "../../../providers/ProjectFormProvider";
import {ClockCircleOutlined, TagOutlined} from "@ant-design/icons";
import PositionFixedFeeForm from "./PositionFixedFeeForm";
import PositionTimeMaterialsForm from "./PositionTimeMaterialsForm";
import PositionFixedRetainerForm from "./PositionFixedRetainerForm";


const PositionTiming = props => {
    const {auth} = useAuth();

    const {
        project,
        formModel: projectFormModel,
        isLoading: isProjectLoading,
        onFormSubmit: onProjectFormSubmit
    } = useProjectForm();

    const contractType = project.contract_type;
    const {
        formModel: position,
        canSetContractType,
        isProjectUpdating,
        onFormChange,
        onFormReset
    } = usePositionForm();

    const {isDisabled, goBack, onSubmit, onChange} = useOutletContext();

    const contractTypes = TB.invert(laravel.project_contract_types);
    const structureTypeOptions = [
        {
            value: parseInt(contractTypes['Time & Materials']),
            Icon: <ClockCircleOutlined style={{fontSize: 22}}/>,
            title: 'Time & Materials',
            description: 'An hourly rate.',
        },
        {
            value: parseInt(contractTypes['Fixed Fee']),
            Icon: <TagOutlined style={{fontSize: 22}}/>,
            title: 'Fixed Fee Project',
            description: 'Fixed-price contract, based upon a set of deliverables and start/end date.',
        },
        {
            value: parseInt(contractTypes['Fixed Retainer']),
            Icon: <i className="fas fa-user-clock text-[22px]"/>,
            title: 'Retainer',
            description: 'Ongoing, fixed weekly or monthly retainer based upon job description and activities.',
        },
    ]

    const selectedContractType = TB.find(structureTypeOptions, ['value', parseInt(contractType)])

    const startMinDate = useMemo(() => {
        if (auth?.canManageClients()) {
            return undefined
        }

        let minDate = new Date();

        if (position.started_at && TB.moment(position.started_at).clone().endOf('day').toDate() < minDate) {
            return TB.moment(position.started_at).clone().endOf('day').toDate()
        }

        return minDate;
    }, [position.start_date]);

    // canSetContractType
    const changeContractType = async (type) => {
        const newKeys = {
            [Project.ContractType.FixedFee]: 1,
            [Project.ContractType.TimeMaterials]: 2,
            [Project.ContractType.FixedRetainer]: 3
        }
        if (Project.contractTypeNameFor(type) === Project.ContractType.TimeMaterials) {
            await onFormChange({
                deliverables: undefined,
                payout_frequency: undefined,
                actual_total_periods: undefined,
                avg_hours_per_period: undefined,
                payout_amount: undefined,
                required_onsite: undefined,
                onsite_days_per_week: undefined,
                budget: undefined,
            })
        }
        if (Project.contractTypeNameFor(type) === Project.ContractType.FixedFee) {
            await onFormChange({
                deliverables: position.deliverables || [],
                payout_frequency: undefined,
                actual_total_periods: undefined,
                avg_hours_per_period: undefined,
                payout_amount: undefined,
                required_onsite: undefined,
                onsite_days_per_week: undefined,
                budget: undefined,
            })
        }
        if (Project.contractTypeNameFor(type) === Project.ContractType.FixedRetainer) {
            await onFormChange({
                deliverables: position.deliverables || [],
                payout_frequency: position.payout_frequency || 'weekly',
                actual_total_periods: undefined,
                avg_hours_per_period: undefined,
                required_onsite: undefined,
                onsite_days_per_week: undefined,
                budget: undefined,
            })
        }
        await onProjectFormSubmit({contract_type: type})
    }
    const isFixedFee = Project.contractTypeNameFor(contractType) === Project.ContractType.FixedFee;
    const isFixedRetainer = Project.contractTypeNameFor(contractType) === Project.ContractType.FixedRetainer;
    const isTimeMaterials = Project.contractTypeNameFor(contractType) === Project.ContractType.TimeMaterials;

    return (
        <div className="flex flex-col justify-start items-start self-stretch relative gap-4">
            <div className="flex flex-col justify-start items-stretch self-stretch relative gap-3">
                {canSetContractType && (
                    <React.Fragment>

                        <p className="text-xl font-medium text-left text-[#3f434a]">
                            What pricing structure do you prefer?
                        </p>

                        <Radio.Group
                            className="mb-5"
                            onChange={(e) => {
                                console.log('radio checked', e.target.value);
                                return changeContractType(e.target.value);
                            }}
                            disabled={isDisabled || isProjectUpdating}
                            value={contractType || undefined}
                            optionType="button"
                            buttonStyle="solid"
                        >
                            <div className="flex flex-row flex-wrap items-stretch">
                                {structureTypeOptions.map(({value, title, description, Icon}) => {
                                    return (
                                        <Radio key={`contract-type-${value}`} value={value} className="flex flex-row items-start h-[unset] py-3 min-w-72 basis-1/3">
                                            <div className="flex flex-row items-start gap-4">
                                                {Icon}
                                                <div className="flex flex-col gap-1 items-stretch">
                                                    <span className="text-sm font-semibold">
                                                        {title}
                                                    </span>
                                                    <span className="text-[0.65rem] leading-normal line-clamp-3">
                                                        {description}
                                                    </span>
                                                </div>
                                            </div>
                                        </Radio>
                                    )
                                })}
                            </div>


                        </Radio.Group>

                    </React.Fragment>
                )}
            </div>

            {!canSetContractType && selectedContractType && (
                <React.Fragment>
                    <Radio.Group
                        className="pointer-events-none"
                        onChange={(e) => {
                            return
                        }}
                        disabled={isDisabled || isProjectUpdating}
                        value={contractType || undefined}
                        optionType="button"
                        buttonStyle="solid"
                    >
                        <div className="flex flex-row flex-wrap items-stretch">
                            <Radio value={selectedContractType.value} className="flex flex-row items-start h-[unset] py-3 min-w-72 basis-1/3">
                                <div className="flex flex-row items-start gap-4">
                                    {selectedContractType.Icon}
                                    <div className="flex flex-col gap-1 items-stretch">
                                        <span className="text-sm font-semibold">
                                            {selectedContractType.title}
                                        </span>
                                        <span className="text-[0.65rem] leading-normal min-w-64 line-clamp-3">
                                            {selectedContractType.description}
                                        </span>
                                    </div>
                                </div>
                            </Radio>
                        </div>


                    </Radio.Group>

                </React.Fragment>
            )}

            {!TB.isDefined(contractType) && <hr className="self-stretch w-full"/>}

            {!isFixedRetainer && (
                <div className={TB.mapClass("flex flex-col items-stretch self-stretch gap-4", {
                    "hidden": !TB.isDefined(contractType)
                })}>
                    {!props.isReadOnly && (
                        <p className="text-lg font-medium text-left text-[#3f434a]">
                            {/* Timing */}
                            What is the estimated timing for this position?
                        </p>
                    )}

                    <div className="flex flex-row flex-wrap items-start self-stretch gap-x-6 p-3 rounded-lg bg-gray-50">
                        <div className="flex flex-col items-stretch basis-1/3">
                            <FormInputComponent
                                fieldName="started_at"
                                label="Estimated Project Start Date"
                                value={position.started_at}
                                type="date"
                                isReadOnly={props.isReadOnly}
                                suffixIcon={props.isReadOnly ? null : undefined}
                                minDate={startMinDate}
                                required={true}
                                disabled={isDisabled || isProjectUpdating}
                                validateBeforeSubmit={false}
                            />
                        </div>
                        <div className="flex flex-col items-stretch basis-1/3">
                            <FormInputComponent
                                fieldName="ended_at"
                                label="Estimated Project End Date"
                                value={position.ended_at}
                                type="date"
                                isReadOnly={props.isReadOnly}
                                suffixIcon={props.isReadOnly ? null : undefined}
                                disabled={isDisabled || isProjectUpdating}
                                minDate={position.started_at ? TB.moment(position.started_at)
                                    .clone()
                                    .endOf('week')
                                    .toDate() : undefined}
                                required={true}
                                validateBeforeSubmit={false}
                            />
                        </div>
                        {isTimeMaterials && (
                            <div className="basis-full flex flex-row items-center self-stretch gap-4 min-h-[80px]">
                                <div className="flex flex-col items-stretch">
                                    <FormInputComponent
                                        fieldName="is_contract_to_hire"
                                        value={position.is_contract_to_hire}
                                        isReadOnly={props.isReadOnly}
                                        label="Potential for contract to hire?"
                                        type="checkbox"
                                        required={false}
                                        className="whitespace-nowrap"
                                        checkboxClass="whitespace-nowrap"
                                    />
                                </div>
                                <div className="flex flex-col items-stretch">
                                    <FormInputComponent
                                        fieldName="required_onsite"
                                        isReadOnly={props.isReadOnly}
                                        value={position.required_onsite}
                                        label="Requires working on site"
                                        type="checkbox"
                                        required={false}
                                        className="whitespace-nowrap"
                                        checkboxClass="whitespace-nowrap"
                                    />
                                </div>
                                <div className="flex flex-col items-stretch flex-grow">
                                    {position.required_onsite && (
                                        <FormInputComponent
                                            fieldName="onsite_days_per_week"
                                            value={position.onsite_days_per_week}
                                            label="Days / Week on-site"
                                            type="antd-input"
                                            isReadOnly={props.isReadOnly}
                                            placeholder={position.required_onsite ? '# of days on-site' : 'N/A'}
                                            disabled={isDisabled}
                                            pattern={/^\b([1-7])\b/}
                                            required={true}
                                            max={7}
                                            min={1}
                                            validation={{
                                                rules: {
                                                    matchRegexp: /^\b([1-7])\b/
                                                },
                                                errors: {
                                                    matchRegexp: "The # of days on-site must be between 1 and 7",
                                                }
                                            }}
                                        >
                                            <InputNumber
                                                controls={false}
                                                className="w-full"
                                                // bordered={!props.isReadOnly}
                                                placeholder="1-7 days"
                                                pattern={/^\b([1-7])\b/}
                                                required={true}
                                            />
                                        </FormInputComponent>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            )}

            {TB.isDefined(contractType) && <hr className="self-stretch w-full"/>}

            <div className={TB.mapClass("self-stretch", {
                "hidden": !TB.isDefined(contractType) || (!isFixedRetainer && !position.hasSetDates())
            })}>
                {isTimeMaterials && (
                    <PositionTimeMaterialsForm
                        isDisabled={isDisabled}
                        isReadOnly={props.isReadOnly}/>
                )}

                {isFixedFee && (
                    <PositionFixedFeeForm
                        isDisabled={isDisabled}
                        isReadOnly={props.isReadOnly}/>
                )}
                {isFixedRetainer && (
                    <PositionFixedRetainerForm
                        isDisabled={isDisabled}
                        isReadOnly={props.isReadOnly}/>
                )}
            </div>


        </div>
    );
};

PositionTiming.propTypes = {};

export default PositionTiming;